// Theme variables are edited per theme (see PosterThemePage) and stored as a
// `variables` array on the posterTheme document — the platform's set on a shared theme,
// and each venue's overrides on its own document for that theme, with the venue's
// overriding the shared set at render time. The resolved variables are collected into
// a name→value lookup (a ThemeRecord) that is threaded through the render tree alongside
// the field record, so bound properties and image sources resolve against it — e.g.
// `logo` for an image, `address` in a text template — without polluting the record.

import type { Poster, PosterObject, Style, StyleValue } from './posterSchema';

/** A theme's resolved variables as a name→value lookup (see themeVariablesToRecord). */
export type ThemeRecord = Record<string, unknown>;

/** A single theme variable as stored in a posterTheme document's `variables` array. */
export interface ThemeVariable {
	name:   string;
	type?:  'string' | 'image';
	value:  unknown;
	/* True when the variable comes from a shared theme rather than a venue's own overrides —
	   its image asset lives under __SHARED, not the site key. Set by the server when
	   assembling the resolved variable list. */
	shared?: boolean;
}

/**
 * True when a variable holds an uploaded image rather than a string. `type` says so
 * outright, and an image value (which carries a hash) settles the documents written
 * before `type` was recorded.
 */
export function isImageVariable(variable: {type?: string; value: unknown}): boolean
{
	return variable.type === 'image'
		|| (variable.value != null && typeof variable.value === 'object' && 'hash' in variable.value);
}

/**
 * Folds a theme's `variables` array into a plain lookup object keyed by variable
 * name. String variables yield their string; image variables yield the stored
 * image value ({hash, formats, …}) that ImageObject knows how to render.
 * A shared image value carries a `shared` marker so the renderer reads it from __SHARED.
 */
export function themeVariablesToRecord(variables: ThemeVariable[] | undefined): ThemeRecord
{
	const record: ThemeRecord = {};
	for (const variable of variables ?? [])
		if (variable.name) {
			const value = variable.value;
			record[variable.name] = variable.shared && value != null && typeof value === 'object'
				? {...value as Record<string, unknown>, shared: true}
				: value;
		}
	return record;
}

/**
 * Every theme variable name a design binds to: the canvas' bindings plus those of every object,
 * nested containers included. A variable named in this set is in use — deleting it would leave
 * those bindings resolving to nothing (see PosterThemePage, which refuses the deletion).
 */
export function designVariableNames(design: Poster): Set<string>
{
	const names = new Set<string>();
	addBoundNames(design.canvas?.bindings, names);
	addObjectBoundNames(design.objects ?? [], names);
	return names;
}

function addObjectBoundNames(objects: PosterObject[], names: Set<string>): void
{
	for (const object of objects) {
		addBoundNames(object.bindings, names);
		if (object.type === 'container')
			addObjectBoundNames(object.objects ?? [], names);
	}
}

function addBoundNames(bindings: Record<string, string> | undefined, names: Set<string>): void
{
	for (const name of Object.values(bindings ?? {}))
		if (name)
			names.add(name);
}

/**
 * Style keys whose value feeds layout arithmetic (font metrics, line boxes, borders) and so must
 * resolve to a number. A binding is the one place a value of the wrong type can reach these — the
 * bindings popup will pair any variable with any key, so a font *name* bound to `fontSize` reaches
 * the geometry as NaN. That does not stay local: one NaN child height poisons the sum in
 * computeFlexColumn, so every sibling's top becomes NaN and the invalid transform stacks the whole
 * column at the container's origin. Keys left off this list are string-tolerant by design — `rx`
 * takes "50%", `fontWeight` takes "bold", and the image filters parse their own values.
 */
const NUMERIC_STYLE_KEYS = new Set(['fontSize', 'lineHeight', 'letterSpacing', 'strokeWidth']);

/**
 * The value a bound style key takes, or undefined when the variable cannot supply one. A numeric
 * key accepts a number or a numeric string ("24"); anything else leaves the binding unapplied, so
 * the object keeps its own designed value instead of rendering with a broken layout.
 */
function boundStyleValue(key: string, value: unknown): StyleValue | undefined
{
	if (value === undefined) return undefined;
	if (!NUMERIC_STYLE_KEYS.has(key)) return value as StyleValue;
	const num = typeof value === 'number' ? value : parseFloat(value as string);
	return Number.isFinite(num) ? num : undefined;
}

/**
 * Returns `object.style` with each bound style key replaced by the value of the theme
 * variable it is bound to (looked up from the theme lookup). Bindings to `src`/`field`
 * are ignored here — those are image data sources, not style (see boundImageFieldPath).
 * A value that does not fit the key is dropped (see boundStyleValue).
 * Returns the original style reference when nothing applies so memo identity stays stable
 * and the render does not churn.
 */
export function resolveEffectiveStyle(object: { style?: Style; bindings?: Record<string, string> }, theme: ThemeRecord | undefined): Style | undefined
{
	const bindings = object.bindings;
	if (!bindings || !theme) return object.style;

	let result: Style | undefined;
	for (const [key, varName] of Object.entries(bindings)) {
		if (key === 'src' || key === 'field') continue;
		const value = boundStyleValue(key, theme[varName]);
		if (value === undefined) continue;
		result ??= { ...(object.style ?? {}) };
		result[key] = value;
	}
	return result ?? object.style;
}

/** Returns the value of a named theme variable from the theme lookup, or undefined. */
export function resolveThemeVar(theme: ThemeRecord | undefined, varName: string | undefined): unknown
{
	if (!varName || !theme) return undefined;
	return theme[varName];
}

/**
 * When an image object binds its source (`src`/`field`) to a theme variable, returns that
 * variable's name (the key into the theme lookup); otherwise null.
 */
export function boundImageVarName(object: { bindings?: Record<string, string> }): string | null
{
	return object.bindings?.field ?? object.bindings?.src ?? null;
}

/**
 * When an image object binds its source to a theme variable, returns the dotted field
 * path ("theme.<varName>") — the folder-routing key handed to fieldImageUrl (theme images
 * live under PosterTheme-image); otherwise null. The variable's value is resolved
 * separately from the theme lookup (see boundImageVarName / resolveThemeVar).
 */
export function boundImageFieldPath(object: { bindings?: Record<string, string> }): string | null
{
	const varName = boundImageVarName(object);
	return varName ? `theme.${varName}` : null;
}
